import React from 'react';
import {
  R2_RESET_NOTICE,
  describeStatus,
  formatDaysLate,
  recordSourceLabel,
  type RecordSource,
  type StatusDocumentKind,
} from '@/domain/warehouse';
import './StatusChip.css';

type StatusChipProps = {
  kind: StatusDocumentKind;
  status: string | null | undefined;
  /** Overrides the mapped label (the tone still comes from the map). */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
};

/** A status in the shared Warehouse/Invoicing language: sentence case, one tone per stage. */
export function StatusChip({ kind, status, label, size = 'md', className }: StatusChipProps) {
  const description = describeStatus(kind, status);
  return (
    <span
      className={className ? `status-chip ${className}` : 'status-chip'}
      data-tone={description.tone}
      data-status={description.value || undefined}
      data-size={size === 'sm' ? 'sm' : undefined}
    >
      {label ?? description.label}
    </span>
  );
}

/** "R2" or "Portal" beside a document number. */
export function SourceTag({ source, className }: { source: RecordSource | null | undefined; className?: string }) {
  const label = recordSourceLabel(source);
  return (
    <span
      className={className ? `source-tag ${className}` : 'source-tag'}
      data-source={source === 'r2' ? 'r2' : 'portal'}
      title={source === 'r2' ? 'Managed in R2 until cutover' : 'Created in the Portal'}
    >
      {label}
    </span>
  );
}

/** "2 days late" in amber; renders nothing when the date is not late. */
export function LateDate({ daysLate, className }: { daysLate: number | null | undefined; className?: string }) {
  const text = formatDaysLate(daysLate);
  if (!text) return null;
  return <span className={className ? `late-date ${className}` : 'late-date'}>{text}</span>;
}

type R2ManagedBannerProps = {
  reason: string;
  /** Last time R2's nightly sync touched the record, already formatted; hidden when unknown. */
  lastSyncLabel?: string | null;
  className?: string;
};

/** One-line reason on a record R2 owns, with the last sync time when it is known. */
export function R2ManagedBanner({ reason, lastSyncLabel, className }: R2ManagedBannerProps) {
  return (
    <p className={className ? `r2-managed-banner ${className}` : 'r2-managed-banner'} role="note">
      <span className="r2-managed-banner__text">{reason}</span>
      {lastSyncLabel ? <span className="r2-managed-banner__sync">Last R2 sync: {lastSyncLabel}</span> : null}
    </p>
  );
}

/** The one-line warning inside action dialogs on R2 purchase orders and master bills. */
export function R2ResetNotice({ className }: { className?: string }) {
  return (
    <p className={className ? `r2-reset-notice ${className}` : 'r2-reset-notice'} role="note">
      {R2_RESET_NOTICE}
    </p>
  );
}

export default StatusChip;
